import { nuitEtRappelsPermis } from '@nextilt/commun';
import type { EnvoiDAbonnement, ReglagesDesNotifications, ReponseEssai } from '../partage/api/types';
import { heureLisible } from '../partage/datesEnMots';
import {
  ESSAI_ECHEC,
  ESSAI_PARTI,
  ESSAI_PERDU,
  NOTIFICATIONS_IMPOSSIBLES,
  NOTIFICATIONS_REFUSEES,
  PAS_DE_RESEAU,
  nuitEnMots,
} from '../partage/vocabulaire';

/**
 * Les règles du panneau Notifications du téléphone (chantier notifications-socle, ticket 03),
 * sans React ni navigateur : ce que dit l'interrupteur de cet appareil, ce qui part au serveur
 * pour l'abonner, les heures de la nuit. Ce qui parle au navigateur : `cetAppareil.ts` ; le
 * rendu : `PanneauDesNotifications.tsx`.
 */

/**
 * Ce que le panneau montre en tête : l'interrupteur (abonné ou non), ou une phrase à sa place
 * quand il ne peut rien répondre (spec du chantier, « S'abonner, au téléphone »).
 */
export type EtatDeCetAppareil = 'impossible' | 'refusee' | 'hors-ligne' | 'abonne' | 'pas-abonne';

export interface CeQueDitLeNavigateur {
  /** Service worker, abonnement et notifications existent (`notificationsPossibles`). */
  possible: boolean;
  /** `Notification.permission`. */
  permission: 'default' | 'granted' | 'denied';
  /** `navigator.onLine`. */
  enLigne: boolean;
  /** Un abonnement est présent dans le navigateur de cet appareil, pas seulement au compte. */
  abonne: boolean;
}

export function etatDeCetAppareil({ possible, permission, enLigne, abonne }: CeQueDitLeNavigateur): EtatDeCetAppareil {
  if (!possible) return 'impossible';
  if (permission === 'denied') return 'refusee';
  if (!enLigne) return 'hors-ligne';
  return abonne ? 'abonne' : 'pas-abonne';
}

/** La phrase à la place de l'interrupteur, jamais en rouge ; `null` quand l'interrupteur se montre. */
export function phraseDeLEtat(etat: EtatDeCetAppareil): string | null {
  switch (etat) {
    case 'impossible':
      return NOTIFICATIONS_IMPOSSIBLES;
    case 'refusee':
      return NOTIFICATIONS_REFUSEES;
    case 'hors-ligne':
      return PAS_DE_RESEAU;
    default:
      return null;
  }
}

/**
 * Le navigateur sait recevoir des notifications : un service worker, l'abonnement (`PushManager`)
 * et les notifications. Un iPhone dont l'app n'est pas ajoutée à l'écran d'accueil n'a pas les
 * deux derniers.
 */
export function notificationsPossibles(fenetre: { navigator?: object; PushManager?: unknown; Notification?: unknown }): boolean {
  return (
    !!fenetre.navigator &&
    'serviceWorker' in fenetre.navigator &&
    typeof fenetre.PushManager === 'function' &&
    typeof fenetre.Notification === 'function'
  );
}

/** L'abonnement que donne le navigateur (`PushSubscription.toJSON()`), à la forme qu'il a. */
export interface AbonnementDuNavigateur {
  endpoint?: string | null;
  keys?: Record<string, string | undefined>;
}

/** Ce qui part au serveur pour abonner cet appareil ; `null` si le navigateur n'a pas tout donné. */
export function envoiDeLAbonnement(abonnement: AbonnementDuNavigateur, fuseau: string): EnvoiDAbonnement | null {
  const p256dh = abonnement.keys?.p256dh;
  const auth = abonnement.keys?.auth;
  if (!abonnement.endpoint || !p256dh || !auth) return null;
  return { adresse: abonnement.endpoint, cles: { p256dh, auth }, fuseau };
}

/** La clé publique du serveur (base64url), en octets, comme `pushManager.subscribe` la veut. */
export function cleEnOctets(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const complete = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binaire = atob(complete);
  const octets = new Uint8Array(new ArrayBuffer(binaire.length));
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return octets;
}

/** Ce que dit le panneau après l'essai. */
export function phraseDeLEssai(envoi: ReponseEssai['envoi']): string {
  if (envoi === 'parti') return ESSAI_PARTI;
  if (envoi === 'abonnement_perdu') return ESSAI_PERDU;
  return ESSAI_ECHEC;
}

/** Une heure pleine de la nuit, à la française : « 23 h », « 6 h ». */
export function heureDeLaNuit(heure: number): string {
  return heureLisible(`${heure}:00`);
}

/** « De 23 h à 6 h ». */
export function motDeLaNuit(debut: number, fin: number): string {
  return nuitEnMots(heureDeLaNuit(debut), heureDeLaNuit(fin));
}

/** Ce que les listes d'heures regardent dans les réglages du compte. */
export type NuitEtRappels = Pick<ReglagesDesNotifications, 'nuitDebut' | 'nuitFin' | 'heureDesRappels'>;

const HEURES_PLEINES = Array.from({ length: 24 }, (_, h) => h);

/**
 * Les heures pleines où une borne de la nuit peut se poser : celles que le serveur accepte, une
 * nuit qui dure et l'heure des rappels du jour dehors (`nuitEtRappelsPermis`, `@nextilt/commun`).
 */
export function heuresDeLaNuit(borne: 'debut' | 'fin', r: NuitEtRappels): number[] {
  return HEURES_PLEINES.filter((h) =>
    nuitEtRappelsPermis(borne === 'debut' ? { debut: h, fin: r.nuitFin } : { debut: r.nuitDebut, fin: h }, r.heureDesRappels)
  );
}

/** Les heures pleines où les rappels du jour peuvent partir : hors de la nuit réglée. */
export function heuresDesRappels(r: NuitEtRappels): number[] {
  return HEURES_PLEINES.filter((h) => nuitEtRappelsPermis({ debut: r.nuitDebut, fin: r.nuitFin }, h));
}

/**
 * L'abonnement du navigateur a été fait avec une autre clé publique que celle du serveur
 * (`PushSubscription.options.applicationServerKey`) : il faut le refaire. Un navigateur qui ne
 * dit pas sa clé (`null`) ne se réabonne pas : sinon il le ferait à chaque ouverture.
 */
export function faitAvecUneAutreCle(cleDuNavigateur: ArrayBuffer | null | undefined, clePublique: string): boolean {
  if (!cleDuNavigateur) return false;
  const attendue = cleEnOctets(clePublique);
  const lue = new Uint8Array(cleDuNavigateur);
  return lue.length !== attendue.length || lue.some((octet, i) => octet !== attendue[i]);
}
